import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
});

// Context-aware API helper - builds URL based on context (business/personal)
const getContextUrl = (context, path) => {
  if (context === 'personal') {
    return `${BACKEND_URL}/api/personal${path}`;
  }
  return `${BACKEND_URL}/api/business${path}`;
};

// Context-aware API calls
export const getContextDashboardStats = (context = 'business') => 
  axios.get(getContextUrl(context, '/dashboard/stats'), { withCredentials: true });

export const getContextTransactions = (context = 'business', params = {}) => 
  axios.get(getContextUrl(context, '/transactions'), { params, withCredentials: true });

export const createContextTransaction = (context = 'business', data) => 
  axios.post(getContextUrl(context, '/transactions'), data, { withCredentials: true });

export const getContextReceipts = (context = 'business', params = {}) => 
  axios.get(getContextUrl(context, '/receipts'), { params, withCredentials: true });

export const getContextMileage = (context = 'business', params = {}) => 
  axios.get(getContextUrl(context, '/mileage'), { params, withCredentials: true });

export const createContextMileage = (context = 'business', data) => 
  axios.post(getContextUrl(context, '/mileage'), data, { withCredentials: true });

export const getContextDocuments = (context = 'business', params = {}) => 
  axios.get(getContextUrl(context, '/documents'), { params, withCredentials: true });

// Dashboard (legacy - kept for backward compatibility)
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentPending = () => api.get('/dashboard/recent');

// Auth
export const createAuthSession = (sessionId) => api.post('/auth/session', null, { params: { session_id: sessionId } });
export const getCurrentUser = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// User
export const getUser = () => api.get('/user');
export const updateUser = (data) => api.put('/user', null, { params: data });

// Transactions
export const getTransactions = (params) => api.get('/transactions', { params });
export const createTransaction = (data) => api.post('/transactions', data);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const categorizeTransaction = (id, category, taxCategory) => 
  api.post(`/transactions/${id}/categorize`, null, { params: { category, tax_category: taxCategory } });
export const addTag = (id, tag) => api.post(`/transactions/${id}/tags`, null, { params: { tag } });
export const removeTag = (id, tag) => api.delete(`/transactions/${id}/tags/${tag}`);

// CSV Import
export const importCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const previewCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/csv/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Plaid
export const createPlaidLinkToken = () => api.post('/plaid/link-token');
export const getPlaidLinkToken = () => api.post('/plaid/link-token');
export const exchangePlaidToken = (publicToken, institutionName) => 
  api.post('/plaid/exchange', null, { params: { public_token: publicToken, institution_name: institutionName } });
export const syncPlaidTransactions = () => api.post('/plaid/sync');
export const getPlaidStatus = () => api.get('/plaid/status');
export const getLinkedAccounts = () => api.get('/plaid/linked-accounts');
export const unlinkAccount = (accountId) => api.delete(`/plaid/accounts/${accountId}`);
export const triggerAutoSync = () => api.post('/plaid/auto-sync/trigger');
export const getSyncHistory = (limit = 10) => api.get('/plaid/sync-history', { params: { limit } });

// Receipts
export const getReceipts = (status) => api.get('/receipts', { params: { status } });
export const uploadReceipt = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/receipts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const processReceiptOCR = (id) => api.post(`/receipts/${id}/ocr`);
export const autoMatchReceipt = (id) => api.post(`/receipts/${id}/match`);
export const linkReceiptToTransaction = (receiptId, transactionId) => 
  api.post(`/receipts/${receiptId}/link/${transactionId}`);

// Mileage
export const getMileageEntries = (year) => api.get('/mileage', { params: { year } });
export const createMileageEntry = (data) => api.post('/mileage', data);
export const deleteMileageEntry = (id) => api.delete(`/mileage/${id}`);
export const getMileagePresets = () => api.get('/mileage/presets');
export const createMileagePreset = (name, address) => 
  api.post('/mileage/presets', null, { params: { name, address } });
export const exportMileagePDF = (year) => 
  api.get('/mileage/export/pdf', { params: { year }, responseType: 'blob' });
export const calculateDistance = (startAddress, endAddress) =>
  api.post('/mileage/calculate-distance', null, { params: { start_address: startAddress, end_address: endAddress } });

// Frequent Trips
export const getFrequentTrips = () => api.get('/mileage/frequent-trips');
export const createFrequentTrip = (data) => 
  api.post('/mileage/frequent-trips', null, { params: data });
export const logFrequentTrip = (tripId) => 
  api.post(`/mileage/frequent-trips/${tripId}/log`);
export const deleteFrequentTrip = (tripId) => 
  api.delete(`/mileage/frequent-trips/${tripId}`);

// Smart Trip Suggestions
export const getTripSuggestions = () => api.get('/mileage/suggestions');
export const dismissSuggestion = (startLocation, endLocation) => 
  api.post('/mileage/suggestions/dismiss', null, { 
    params: { start_location: startLocation, end_location: endLocation } 
  });

// Write-offs
export const getWriteoffTemplates = () => api.get('/writeoffs/templates');
export const getIndustryTemplate = (industry) => api.get(`/writeoffs/template/${industry}`);
export const getWriteoffs = (year) => api.get('/writeoffs', { params: { year } });
export const createWriteoff = (data) => 
  api.post('/writeoffs', null, { params: data });
export const updateWriteoff = (id, data) => 
  api.put(`/writeoffs/${id}`, null, { params: data });
export const calculateQuarterlyTax = (income, expenses, year) => 
  api.get('/writeoffs/quarterly-tax', { params: { income, expenses, year } });

// Tax Database & Search
export const getAllTaxForms = () => api.get('/tax-forms');
export const searchTaxForms = (query) => api.get('/tax-forms/search', { params: { q: query } });
export const getTaxForm = (formId) => api.get(`/tax-forms/${formId}`);
export const getPopularTaxForms = () => api.get('/tax-forms/popular');
export const getWriteoffsDatabase = () => api.get('/writeoffs-database');
export const searchWriteoffs = (query, businessType) => 
  api.get('/writeoffs-database/search', { params: { q: query, business_type: businessType } });
export const getWriteoffsForBusinessType = (businessType) => 
  api.get(`/writeoffs-database/business-type/${businessType}`);
export const getBusinessTypes = () => api.get('/writeoffs-database/business-types');
export const getPopularWriteoffs = () => api.get('/writeoffs/popular');
export const getBusinessDocuments = () => api.get('/business-documents');
export const searchBusinessDocuments = (query) => api.get('/business-documents/search', { params: { q: query } });

// Reports
export const getScheduleCReport = (year) => api.get('/reports/schedule-c', { params: { year } });
export const exportTransactionsCSV = (params) => 
  api.get('/reports/export/transactions/csv', { params, responseType: 'blob' });
export const exportTransactionsExcel = (params) => 
  api.get('/reports/export/transactions/excel', { params, responseType: 'blob' });
export const exportScheduleCCSV = (year) => 
  api.get('/reports/export/schedule-c/csv', { params: { year }, responseType: 'blob' });
export const exportScheduleCPDF = (year) => 
  api.get('/reports/export/schedule-c/pdf', { params: { year }, responseType: 'blob' });
export const getExpenseReportTemplate = (includeSampleData, year) =>
  api.get('/reports/expense-template', { params: { include_sample_data: includeSampleData, year }, responseType: 'blob' });

// Schedule C Save & History
export const saveScheduleC = (data) => api.post('/reports/schedule-c/save', data);
export const updateScheduleC = (versionId, data) => api.put(`/reports/schedule-c/${versionId}`, data);
export const getScheduleCHistory = (year, limit = 20) => api.get('/reports/schedule-c/history', { params: { year, limit } });
export const getSavedScheduleC = (versionId) => api.get(`/reports/schedule-c/saved/${versionId}`);
export const getLatestScheduleC = (year) => api.get(`/reports/schedule-c/latest/${year}`);
export const deleteScheduleC = (versionId) => api.delete(`/reports/schedule-c/${versionId}`);
export const exportScheduleCHistory = (year, format = 'csv') => 
  api.get('/reports/schedule-c/history/export', { params: { year, format }, responseType: 'blob' });

// Categories & Tags
export const getScheduleCCategories = () => api.get('/categories');
export const getAllTags = () => api.get('/tags');
export const getUnmatchedTransactions = () => api.get('/transactions/unmatched');

// Business Profile
export const updateBusinessProfile = (data) => api.put('/user/business-profile', null, { params: data });

// Documents
export const getDocuments = (params) => api.get('/documents', { params });
export const getDocumentTypes = () => api.get('/documents/types');
export const uploadDocument = (file, metadata) => {
  const formData = new FormData();
  formData.append('file', file);
  Object.keys(metadata).forEach(key => {
    if (metadata[key] !== null && metadata[key] !== undefined) {
      formData.append(key, metadata[key]);
    }
  });
  return api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const downloadDocument = (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' });
export const unlinkReceipt = (id) => api.delete(`/receipts/${id}/unlink`);
export const moveDocument = (id, folderId) => api.post(`/documents/${id}/move`, null, { params: { folder_id: folderId } });
export const createShareLink = (id, expiryDays) => api.post(`/documents/${id}/share`, null, { params: { expiry_days: expiryDays } });
export const revokeShareLink = (id) => api.delete(`/documents/${id}/share`);
export const getDocumentForPrint = (id) => api.get(`/documents/${id}/print`);

// Folders
export const getFolders = (parentId, includeChildren) => {
  const params = {};
  if (parentId !== undefined) params.parent_id = parentId;
  if (includeChildren) params.include_children = true;
  return api.get('/folders', { params });
};
export const getFolder = (id) => api.get(`/folders/${id}`);
export const getFolderChildren = (id) => api.get(`/folders/${id}/children`);
export const createFolder = (data) => api.post('/folders', null, { params: data });
export const updateFolder = (id, data) => api.put(`/folders/${id}`, null, { params: data });
export const deleteFolder = (id) => api.delete(`/folders/${id}`);
export const getFolderDocuments = (id) => api.get(`/folders/${id}/documents`);

// Data Backup & Export
export const getBackupSummary = () => api.get('/backup/summary');
export const exportDataJson = () => api.get('/backup/export/json', { responseType: 'blob' });
export const exportDataZip = () => api.get('/backup/export/zip', { responseType: 'blob' });

// Notification Preferences
export const getNotificationPreferences = () => api.get('/notifications/preferences');
export const updateNotificationPreferences = (data) => api.put('/notifications/preferences', null, { params: data });
export const getUpcomingDeadlines = () => api.get('/notifications/upcoming-deadlines');
export const deleteAccount = () => api.delete('/account/delete');

// Subscriptions
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const getSubscriptionStatus = () => api.get('/subscription/status');
export const createCheckoutSession = (plan, originUrl) => 
  api.post('/subscription/create-checkout', null, { 
    params: { 
      plan, 
      success_url: `${originUrl}/pricing?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${originUrl}/pricing`
    } 
  });
export const cancelSubscription = () => api.post('/subscription/cancel');
export const createPortalSession = (returnUrl) => 
  api.post('/subscription/portal', null, { params: { return_url: returnUrl } });

// Email Notifications
export const sendTestEmail = (email) => api.post('/email/send-test', null, { params: { email } });
export const sendWeeklySummary = (email) => api.post('/email/send-weekly-summary', null, { params: { email } });
export const sendDeadlineReminder = (params) => api.post('/email/send-deadline-reminder', null, { params });
export const getShoeboxEmail = () => api.get('/email/shoebox-address');
export const getEmailPreferences = () => api.get('/email/preferences');
export const updateEmailPreferences = (prefs) => api.put('/email/preferences', null, { params: prefs });

// Admin
export const checkAdminStatus = () => api.get('/admin/check');
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminUsers = (skip = 0, limit = 50) => api.get('/admin/users', { params: { skip, limit } });
export const updateUserRole = (userId, role, isAdmin) => 
  api.put(`/admin/users/${userId}/role`, null, { params: { role, is_admin: isAdmin } });
export const getAdminActivity = (limit = 20) => api.get('/admin/activity', { params: { limit } });
export const deleteUserAdmin = (userId) => api.delete(`/admin/users/${userId}`);

// Demo Data
export const populateDemoData = () => api.post('/demo/populate');
export const clearDemoData = () => api.delete('/demo/clear');

// Expense Tracker Module
export const getExpenseCategories = () => api.get('/expense-tracker/categories');
export const getExpenseTrackerSummary = (period = 'month') => 
  api.get('/expense-tracker/dashboard/summary', { params: { period } });
export const getExpenseChartData = (period = '6months') => 
  api.get('/expense-tracker/chart-data', { params: { period } });
export const scanReceipt = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/expense-tracker/receipts/scan', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const scanIncomeDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/expense-tracker/income/scan', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const uploadBankStatement = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/expense-tracker/bank-statement/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getPendingBankImports = () => api.get('/expense-tracker/bank-imports');
export const approveBankImports = (transactionIds) => 
  api.post('/expense-tracker/bank-imports/approve', transactionIds);
export const createManualEntry = (data) => api.post('/expense-tracker/manual-entry', data);

// Document Vault
export const getVaultDocuments = (params) => api.get('/expense-tracker/documents', { params });
export const uploadVaultDocument = (file, category, tags) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category || 'uncategorized');
  formData.append('tags', tags || '');
  return api.post('/expense-tracker/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getVaultDocument = (docId) => api.get(`/expense-tracker/documents/${docId}`);
export const downloadVaultDocument = (docId) => 
  api.get(`/expense-tracker/documents/${docId}/download`, { responseType: 'blob' });
export const deleteVaultDocument = (docId) => api.delete(`/expense-tracker/documents/${docId}`);
export const updateVaultDocument = (docId, data) => api.put(`/expense-tracker/documents/${docId}`, data);

// Report Export
export const exportReportCSV = (startDate, endDate, reportType = 'all') =>
  api.get('/expense-tracker/reports/export/csv', { 
    params: { start_date: startDate, end_date: endDate, report_type: reportType },
    responseType: 'blob'
  });
export const exportReportExcel = (startDate, endDate, reportType = 'all') =>
  api.get('/expense-tracker/reports/export/excel', { 
    params: { start_date: startDate, end_date: endDate, report_type: reportType },
    responseType: 'blob'
  });
export const exportReportPDF = (startDate, endDate, reportType = 'all') =>
  api.get('/expense-tracker/reports/export/pdf', { 
    params: { start_date: startDate, end_date: endDate, report_type: reportType },
    responseType: 'blob'
  });

// Digital Shoebox
export const getShoeboxInbox = () => api.get('/shoebox/inbox');
export const getShoeboxEmailAddress = () => api.get('/shoebox/email-address');
export const approveShoeboxItem = (itemId, data) => api.post(`/shoebox/${itemId}/approve`, data);
export const rejectShoeboxItem = (itemId) => api.post(`/shoebox/${itemId}/reject`);
export const deleteShoeboxItem = (itemId) => api.delete(`/shoebox/${itemId}`);
export const forwardToShoebox = (emailData) => api.post('/shoebox/forward', emailData);

// Data Access & Merge
export const getUserDataAccess = () => api.get('/user/data-access');
export const toggleMergeData = (enabled) => api.post('/user/toggle-merge', null, { params: { enabled } });
export const getMergedDashboardStats = () => api.get('/merged/dashboard/stats');
export const getMergedTransactions = (params = {}) => api.get('/merged/transactions', { params });

export default api;
